/**
 * SessionTimeoutModal
 *
 * Warning overlay shown to internal staff at 5 minutes remaining on their
 * 2-hour hard session. Not shown to guides or customers (their sessions are
 * long-lived and managed by the Token Refresh Interceptor, SCRUM-355).
 *
 * - Staff: countdown + "Save your work" message. No extend button.
 *   Modal is not dismissible (user cannot opt out of the hard expiry).
 * - Guides/customers: extend button rendered alongside countdown.
 *
 * SCRUM-366 | MVP 1.15 V2 Epic 4
 */

import React from 'react';
import SessionTimeoutCountdown from './SessionTimeoutCountdown';
import SessionExtendButton from './SessionExtendButton';
import { SESSION_WARNING_SECONDS } from '../../hooks/useSessionTimeout';

/**
 * @param {{
 *   isOpen: boolean,
 *   userCategory: 'internal'|'other',
 *   onExtend: () => void,
 *   onExpired: () => void,
 * }} props
 */
export default function SessionTimeoutModal({ isOpen, userCategory, onExtend, onExpired }) {
  if (!isOpen) { return null; }

  const isInternal = userCategory === 'internal';

  return (
    // Backdrop — not clickable for internal staff (enforces hard expiry)
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      data-testid="session-timeout-modal"
      // Internal staff cannot dismiss by clicking the backdrop
      onClick={isInternal ? undefined : undefined}
      aria-modal="true"
      role="dialog"
      aria-labelledby="session-timeout-title"
    >
      <div
        className="relative bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4"
        // Stop click events from bubbling to backdrop
        onClick={e => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mx-auto mb-4">
          <svg
            className="w-6 h-6 text-amber-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        <h2
          id="session-timeout-title"
          className="text-lg font-semibold text-gray-900 text-center mb-2"
        >
          Your session is about to expire
        </h2>

        <p className="text-sm text-gray-500 text-center mb-4">
          You will be logged out in:
        </p>

        <div className="text-3xl font-mono font-bold text-center text-amber-600 mb-4">
          <SessionTimeoutCountdown
            initialSeconds={SESSION_WARNING_SECONDS}
            onExpired={onExpired}
          />
        </div>

        {isInternal ? (
          <p className="text-sm text-gray-600 text-center">
            Please save your work. Your session cannot be extended.
          </p>
        ) : (
          <div className="flex justify-center">
            <SessionExtendButton onExtend={onExtend} />
          </div>
        )}
      </div>
    </div>
  );
}
